import { supabase } from '../lib/supabase';
import type { User, AuthError } from '@supabase/supabase-js';
import { apiService } from './apiService';

export interface SignUpData {
  email: string;
  password: string;
  fullName: string;
  userType: 'professor' | 'student';
}

export interface SignInData {
  email: string;
  password: string;
}

export class AuthService {
  static async signUp({ email, password, fullName, userType }: SignUpData) {
    try {
      console.log('Starting signup process for:', { email, userType, fullName });
      
      // Validate input
      if (!email || !password || !fullName || !userType) {
        throw new Error('All fields are required');
      }
      
      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters');
      }
      
      if (!['professor', 'student'].includes(userType)) {
        throw new Error('Invalid user type');
      }

      // Sign up the user with metadata
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            user_type: userType,
          }
        }
      });

      if (error) {
        console.error('Supabase signup error:', error);
        throw error;
      }

      console.log('Signup successful, user created:', data.user?.id);
      
      // ALWAYS try to create profile manually as backup
      if (data.user) {
        console.log('Creating profile manually as backup...');
        try {
          await this.ensureProfileExists(data.user.id, email, userType, fullName);
          console.log('Manual profile creation successful');
        } catch (err) {
          console.warn('Manual profile creation failed:', err);
          // Try again after a delay
          setTimeout(async () => {
            try {
              await this.ensureProfileExists(data.user!.id, email, userType, fullName);
              console.log('Delayed profile creation successful');
            } catch (err2) {
              console.error('All profile creation attempts failed:', err2);
            }
          }, 2000);
        }
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Auth signup error:', error);
      return { user: null, error: error as AuthError };
    }
  }

  static async signIn({ email, password }: SignInData) {
    try {
      console.log('Starting signin process for:', email);
      
      if (!email || !password) {
        throw new Error('Email and password are required');
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        console.error('Supabase signin error:', error);
        throw error;
      }

      console.log('Signin successful for user:', data.user?.id);

      // Ensure profile exists after successful sign in
      if (data.user) {
        const profileResult = await this.getUserProfile(data.user.id);
        if (!profileResult.profile) {
          console.log('Profile not found, creating from user metadata');
          await this.ensureProfileExists(
            data.user.id, 
            data.user.email || email, 
            data.user.user_metadata?.user_type || 'student',
            data.user.user_metadata?.full_name || email.split('@')[0]
          );
        }
      }

      return { user: data.user, error: null };
    } catch (error) {
      console.error('Auth signin error:', error);
      return { user: null, error: error as AuthError };
    }
  }

  static async signOut() {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error) {
      return { error: error as AuthError };
    }
  }

  static async getCurrentUser(): Promise<User | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  }

  static async getUserProfile(userId: string) {
    try {
      console.log('Fetching profile for user:', userId);
      
      // Use apiService which handles data transformation
      const { data, error } = await apiService.getUserProfile(userId);

      if (error || !data) {
        console.log('Profile fetch error, attempting to create...');
        
        // Get current user data from auth
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.id === userId) {
          const created = await this.ensureProfileExists(
            user.id,
            user.email || '',
            user.user_metadata?.user_type || 'student',
            user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'
          );
          
          if (created) {
            // Try again after creating profile
            const { data: retryData, error: retryError } = await apiService.getUserProfile(userId);
            
            if (!retryError && retryData) {
              console.log('Profile created and fetched successfully');
              return { profile: retryData, error: null };
            }
            console.error('Failed to fetch profile after creation:', retryError);
          }
        }
        
        console.error('Profile fetch failed:', error);
        return { profile: null, error: error as AuthError };
      }
      
      console.log('Profile fetched successfully:', data.userType);
      return { profile: data, error: null };
    } catch (error) {
      console.error('Unexpected error fetching profile:', error);
      return { profile: null, error: error as AuthError };
    }
  }

  // Helper method to ensure profile exists with better error handling
  static async ensureProfileExists(userId: string, email: string, userType: string, fullName: string) {
    try {
      console.log('Ensuring profile exists for:', { userId, email, userType, fullName });
      
      // Validate inputs
      if (!userId || !email) {
        console.error('Missing required parameters for profile creation');
        return false;
      }
      
      // Ensure userType is valid
      const validUserType = ['professor', 'student'].includes(userType) ? userType as 'professor' | 'student' : 'student';
      const validFullName = fullName?.trim() || email.split('@')[0] || 'User';
      
      // Use apiService to create profile
      const { data, error } = await apiService.createUserProfile({
        id: userId,
        email: email.toLowerCase().trim(),
        user_type: validUserType,
        full_name: validFullName
      });

      if (error) {
        console.error('Failed to create/update profile:', error);
        return false;
      }
      
      console.log('Profile created/updated successfully:', data);
      return true;
    } catch (error) {
      console.error('Unexpected error ensuring profile exists:', error);
      return false;
    }
  }

  static onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      callback(session?.user || null);
    });
  }
}